/**
 * Admin service
 * Provides admin-only operations: voucher management, assignments, invitations
 * Requires admin or support role
 */

import axios, { type AxiosResponse } from 'axios'
import BaseService from './BaseService'
import type { OrganisationRole, UserInvitationStatus } from '@/types/models'
import { require } from '@/utils/guards'

const PREMIUM_API = import.meta.env.VITE_PREMIUM_API_URL

// ============================================================================
// Request/Response Types
// ============================================================================

/** Organisation user data */
export interface OrganisationUser {
  id: string
  email: string
  role: OrganisationRole
}

/** Organisation invitation data */
export interface OrganisationInvitation {
  organisationId: string
  invitedEmail: string
  userInvitationStatus: UserInvitationStatus
  role: OrganisationRole
}

/** Add voucher request */
export interface AddVoucherRequest {
  accommodationId: number
  voucherCode: string
}

/** Add RC voucher request */
export interface AddRCVoucherRequest {
  accommodationId: number
  voucherCode: string
}

/** Add voucher response */
export interface AddVoucherResponse {
  success: boolean
  message?: string
}

/** Delete assignment request */
export interface DeleteAssignmentRequest {
  accommodationId: number
}

/** Invite user request */
export interface InviteUserRequest {
  organisationId: string
  data: {
    invitedEmail: string
    role?: Exclude<OrganisationRole, 'support'>
    language: string
  }
}

/** Invite user response */
export interface InviteUserResponse {
  userAddedToOrganisation: boolean
  userInvited: boolean
  invitationId: string | null
}

/** Join organisation request */
export interface JoinOrganisationRequest {
  organisationId: string
}

/** Reason a user cannot be removed from an organisation */
export type UserOrganisationRemovalBlockReason =
  | 'LAST_ADMIN_OF_ORG_WITH_OTHER_USERS'
  | 'LAST_ADMIN_AND_ORG_OWNS_PAID_PRODUCT'
  | 'LAST_ADMIN_AND_NOT_IN_OTHER_ORGS'

/** Organisation user entry within an admin search result */
export interface OrganisationUserSearchResult {
  id: string | null
  email: string | null
  role: OrganisationRole | null
  removalBlockReason: UserOrganisationRemovalBlockReason | null
}

/** Product/campaign entry within an admin search item */
export interface AdminSearchProduct {
  productName: string
  type: string
  endDate: string | null
}

/** Item (accommodation) entry within an admin search result */
export interface AdminSearchItem {
  itemId: number
  products: AdminSearchProduct[]
}

/** Admin search result entry - one organisation, with its users, invitations, and items */
export interface AdminSearchResult {
  id: string
  name: string
  users: OrganisationUserSearchResult[]
  invitations: OrganisationInvitation[]
  items: AdminSearchItem[]
}

/** Kick user from organisation request */
export interface KickUserFromOrganisationRequest {
  organisationId: string
  userId: string
}

// ============================================================================
// AdminService Class
// ============================================================================

/**
 * Admin operations service
 * Extends BaseService with admin-specific endpoints
 */
class AdminService extends BaseService {
  constructor() {
    super({
      baseUrl: import.meta.env.VITE_ORGANISATION_MANAGEMENT_API_URL,
    })
  }

  /**
   * Add voucher code to accommodation
   * @param request - Voucher request data
   * @returns Promise with voucher add result
   * @throws Error if accommodationId or voucherCode missing
   */
  addVoucher({ accommodationId, voucherCode }: AddVoucherRequest): Promise<AxiosResponse<void>> {
    require(accommodationId)
    require(voucherCode)

    return axios({
      url: `${PREMIUM_API}/admin/plus/${accommodationId}/voucher`,
      method: 'POST',
      data: {
        voucherCode,
      },
      headers: this.studioHeaders(),
    })
  }

  /**
   * Add RC voucher code to accommodation
   * @param request - RC voucher request data
   * @returns Promise with voucher add result
   * @throws Error if accommodationId or voucherCode missing
   */
  addRCVoucher({ accommodationId, voucherCode }: AddRCVoucherRequest): Promise<AxiosResponse<AddVoucherResponse>> {
    if (!accommodationId) {
      throw new Error(`Error in POST admin/rc/${accommodationId}/voucher: No accommodationId provided`)
    }
    if (!voucherCode) {
      throw new Error(`Error in POST admin/rc/${accommodationId}/voucher: No voucherCode provided`)
    }

    return axios({
      url: `${PREMIUM_API}/admin/rc/${accommodationId}/voucher`,
      method: 'POST',
      data: {
        voucherCode,
      },
      headers: {
        'X-Studio-Authorization': this.studioToken,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
  }

  /**
   * Admin search by item (accommodation) id, organisation id, or organisation name
   * @param query - Search query string
   * @returns Promise with matching organisations, including users, invitations, and items
   * @throws Error if query missing
   */
  search(query: string): Promise<AxiosResponse<AdminSearchResult[]>> {
    require(query)

    return axios({
      url: `${this.baseUrl}/admin/search`,
      method: 'GET',
      params: { query },
      headers: this.studioHeaders(),
    })
  }

  /**
   * Delete accommodation assignment
   * @param request - Assignment deletion request
   * @returns Promise with deletion result
   */
  deleteAssignment({ accommodationId }: DeleteAssignmentRequest): Promise<AxiosResponse<void>> {
    return axios({
      url: `${this.baseUrl}/admin/assignments/${accommodationId}`,
      method: 'DELETE',
      headers: this.studioHeaders(),
    })
  }

  /**
   * Invite user to organisation (admin)
   * @param request - Invitation request data
   * @returns Promise with invitation result
   */
  inviteUserAsAdmin({ organisationId, data }: InviteUserRequest): Promise<AxiosResponse<InviteUserResponse>> {
    return axios({
      url: `${this.baseUrl}/admin/organisation/${organisationId}/invite`,
      method: 'POST',
      data: data,
      headers: this.studioHeaders(),
    })
  }

  /**
   * Join organisation as support user
   * @param request - Join request data
   * @returns Promise with join result
   * @throws Error if organisationId missing
   */
  joinOrganisationAsSupport({ organisationId }: JoinOrganisationRequest): Promise<AxiosResponse<void>> {
    require(organisationId)

    return axios({
      url: `${this.baseUrl}/admin/organisation/${organisationId}/join`,
      method: 'POST',
      headers: this.studioHeaders(),
    })
  }

  /**
   * Remove (kick) a user from an organisation
   * @param request - Organisation ID and user ID
   * @returns Promise with removal result
   * @throws Error if organisationId or userId missing
   */
  kickUserFromOrganisation({ organisationId, userId }: KickUserFromOrganisationRequest): Promise<AxiosResponse<void>> {
    require(organisationId)
    require(userId)

    return axios({
      url: `${this.baseUrl}/admin/organisations/${organisationId}/users/${userId}`,
      method: 'DELETE',
      headers: this.studioHeaders(),
    })
  }
}

/** Singleton admin service instance */
export default new AdminService()
